const customColors = require("./src/config/colors");

/** @type {import('tailwindcss').Config} */
module.exports = {
  mode: "jit",
  darkMode: "class",
  content: [
    "./src/app/**/*.{js,jsx,ts,tsx}",
    "./src/features/**/*.{tsx,jsx,ts,js}",
    "./src/components/**/*.{tsx,jsx,ts,js}",
  ],
  presets: [require("nativewind/preset")],
  safelist: [
    {
      pattern:
        /(bg|border|text|stroke|fill)-(primary|secondary|tertiary|error|success|warning|info|typography|outline|background|indicator|prompt|chart|toast|statusBadge|notes)-(0|50|100|200|300|400|500|600|700|800|900|950|white|gray|black|error|warning|muted|success|info|light|dark|player|other|horizontalLine)/,
    },
  ],
  theme: {
    /* CLOSED spacing scale (ratified decision #3 — replace, not extend):
     * 4px grid + named 2px micro + 1px hairline. Off-scale spacing classes
     * (py-2.5 and every fractional step) compile to NOTHING by construction.
     * Sizing axes extend the ladder upward below (Phase-0 sizing addendum).
     * NOTE for the Tailwind-4 migration: keep this block self-contained —
     * it ports mechanically to CSS @theme. */
    spacing: {
      0: "0px",
      micro: "2px",
      px: "1px",
      1: "4px",
      2: "8px",
      3: "12px",
      4: "16px",
      5: "20px",
      6: "24px",
      8: "32px",
      10: "40px",
      12: "48px",
      16: "64px",
    },
    /* CLOSED radius scale (docs/design-system/tokens.md). */
    borderRadius: {
      none: "0px",
      sm: "4px",
      md: "8px",
      lg: "12px",
      /* xl(20px) — bottom-sheet top-corner radius (SE-5401). The shared
       * MobileSheetChrome's "more rounded top curves" exceed lg(12); 20px is
       * the next closed rung up. Closed + enumerated per the design-system
       * RFC; consume via rounded-tl-xl / rounded-tr-xl, never an arbitrary
       * value. */
      xl: "20px",
      full: "9999px",
    },
    extend: {
      /* Sizing ladder (Phase-0 addendum): w/h/size/min/max/basis genuinely
       * need larger layout values than the spacing scale — closed,
       * enumerated, discovered from the inventory. */
      ...(() => {
        const sizing = {
          7: "28px",
          9: "36px",
          11: "44px",
          14: "56px",
          20: "80px",
          24: "96px",
          28: "112px",
          32: "128px",
          40: "160px",
          44: "176px",
          48: "192px",
          52: "208px",
          56: "224px",
          64: "256px",
          72: "288px",
          80: "320px",
          /* 90(360px) — the right-side overlay drawer width (SE-6554, Figma
           * 80:15673). Between the existing 80/96 rungs; enumerated here
           * rather than written as an arbitrary value at the call site, per
           * the closed-ladder rule above. */
          90: "360px",
          96: "384px",
          /* 100(400px) — the add-players drawer width after the Scouting
           * drawer redesign (SE-6945, Figma PC1 362:39276). Enumerated here
           * rather than written as an arbitrary value at the call site, per
           * the closed-ladder rule above. */
          100: "400px",
          120: "480px",
          150: "600px",
        };
        /* Percentage fractions are layout semantics (RFC §1) — Tailwind's
         * width/height scales include them but the min/max axes do NOT, so
         * snapped classes like min-w-1/2 were dead. Complete the axes. */
        const fractions = {
          "1/4": "25%",
          "1/3": "33.333333%",
          "2/5": "40%",
          "1/2": "50%",
          "3/5": "60%",
          "2/3": "66.666667%",
          "3/4": "75%",
          "4/5": "80%",
        };
        return {
          width: {
            ...sizing,
            /* SE-8291 — exact width from Position Node (Figma 97:1433); the
             * approved card (1010:20480, SE-8486) draws the same 118.
             * Axis-specific and component-named so the value cannot become a
             * generic spacing rung by accident. */
            "pitch-position-node": "118px",
            /* SE-8486 — the Pitch view's side panels (Figma 281:2793, rows
             * W04/W05): 344 is not on the sizing scale and `w-80` (320) left
             * both panels 24 px narrower than the frame. Axis-specific and
             * component-named for the same reason as the card width. */
            "pitch-side-panel": "344px",
          },
          height: sizing,
          size: {
            ...sizing,
            /* SE-8486 retired `pitch-player-dot` (6px, read from 97:1433):
             * the Unassigned row's frame (971:20747, row W80) draws no
             * marker, and the viewer's static one is the Dot atom's 8. */
            /* SE-8291 — exact formation chevron box from Pitch (281:2793). */
            "pitch-formation-icon": "18px",
          },
          minWidth: { ...sizing, ...fractions },
          minHeight: { ...sizing, ...fractions },
          maxWidth: {
            ...sizing,
            ...fractions,
            /* SE-8486 — the pitch surface's width (Figma 281:2911, row W42:
             * 570×694). `max-w-xl` (576) was the snap; the surface centres
             * at this width and keeps `aspect-pitch-surface` below it. */
            "pitch-surface": "570px",
            /* SE-8486 — the position pop-out's box (Figma 947:16436 /
             * 383:5278, rows W70/S32): 560 sits between the vendored `md`
             * (510) and `lg` (640) modal caps. A max-width because the
             * modal's `full` size supplies `w-full` and a cap merges under
             * it without a width conflict. */
            "pitch-popout": "560px",
            /* SE-8486 — the Unassigned panel's width (Figma 971:20747, row
             * W78): 640 at x 24 of the pitch column; `max-w-xl` (576) was
             * the snap that aligned it to the surface instead. */
            "pitch-unassigned-panel": "640px",
          },
          maxHeight: { ...sizing, ...fractions },
          flexBasis: sizing,
          /* Inset (top/right/bottom/left) defaults to the spacing scale, but
           * floating overlays anchored above chrome (e.g. the mobile player
           * FAB above the 80px bottom nav, SE-5083) need a few larger,
           * enumerated offsets. Reuse the closed sizing ladder so the inset
           * axis stays a closed vocabulary rather than admitting arbitrary
           * values. */
          inset: sizing,
        };
      })(),
      /* Closed flex-grow ratio tokens (design library). Tailwind ships only
       * `flex-1` (1 1 0%); the squad-planning player table needs integer
       * column ratios 6:2:2:3:3:3 (PLAYER : AGE : GPR : WAGE : EST : READINESS,
       * the rescaled-to-integer form of the Figma 3:1:1:1.5:1.5:1.5) shared by
       * PlayerTableHeaderView + PlayerTableRowView. These name those ratios as
       * closed tokens instead of arbitrary flex-[1.5]/flex-[3] values. Mirrors
       * Tailwind's `flex-1` shorthand form `<n> <n> 0%`. */
      flex: {
        2: "2 2 0%",
        3: "3 3 0%",
        6: "6 6 0%",
      },
      /* flex-grow/shrink 2 — used in contracts player-name column (2× wider
       * than the other flex columns). Not in Tailwind's default scale. */
      flexGrow: {
        2: "2",
      },
      flexShrink: {
        2: "2",
      },
      colors: {
        customBlue: "#0DA6F2",
        customRed: "#dd2c00",
        uptrend: {
          500: "#66B584",
          600: "#1C2B21",
        },
        downtrend: {
          400: "#FDAD74",
          500: "#E77828",
          600: "#412F23",
        },
        primary: {
          0: "rgb(var(--color-primary-0)/<alpha-value>)",
          50: "rgb(var(--color-primary-50)/<alpha-value>)",
          100: "rgb(var(--color-primary-100)/<alpha-value>)",
          200: "rgb(var(--color-primary-200)/<alpha-value>)",
          300: "rgb(var(--color-primary-300)/<alpha-value>)",
          400: "rgb(var(--color-primary-400)/<alpha-value>)",
          500: "rgb(var(--color-primary-500)/<alpha-value>)",
          600: "rgb(var(--color-primary-600)/<alpha-value>)",
          700: "rgb(var(--color-primary-700)/<alpha-value>)",
          800: "rgb(var(--color-primary-800)/<alpha-value>)",
          900: "rgb(var(--color-primary-900)/<alpha-value>)",
          950: "rgb(var(--color-primary-950)/<alpha-value>)",
        },
        secondary: {
          0: "rgb(var(--color-secondary-0)/<alpha-value>)",
          50: "rgb(var(--color-secondary-50)/<alpha-value>)",
          100: "rgb(var(--color-secondary-100)/<alpha-value>)",
          200: "rgb(var(--color-secondary-200)/<alpha-value>)",
          300: "rgb(var(--color-secondary-300)/<alpha-value>)",
          400: "rgb(var(--color-secondary-400)/<alpha-value>)",
          500: "rgb(var(--color-secondary-500)/<alpha-value>)",
          600: "rgb(var(--color-secondary-600)/<alpha-value>)",
          700: "rgb(var(--color-secondary-700)/<alpha-value>)",
          800: "rgb(var(--color-secondary-800)/<alpha-value>)",
          900: "rgb(var(--color-secondary-900)/<alpha-value>)",
          950: "rgb(var(--color-secondary-950)/<alpha-value>)",
        },
        tertiary: {
          50: "rgb(var(--color-tertiary-50)/<alpha-value>)",
          100: "rgb(var(--color-tertiary-100)/<alpha-value>)",
          200: "rgb(var(--color-tertiary-200)/<alpha-value>)",
          300: "rgb(var(--color-tertiary-300)/<alpha-value>)",
          400: "rgb(var(--color-tertiary-400)/<alpha-value>)",
          500: "rgb(var(--color-tertiary-500)/<alpha-value>)",
          600: "rgb(var(--color-tertiary-600)/<alpha-value>)",
          700: "rgb(var(--color-tertiary-700)/<alpha-value>)",
          800: "rgb(var(--color-tertiary-800)/<alpha-value>)",
          900: "rgb(var(--color-tertiary-900)/<alpha-value>)",
          950: "rgb(var(--color-tertiary-950)/<alpha-value>)",
        },
        error: {
          0: "rgb(var(--color-error-0)/<alpha-value>)",
          50: "rgb(var(--color-error-50)/<alpha-value>)",
          100: "rgb(var(--color-error-100)/<alpha-value>)",
          200: "rgb(var(--color-error-200)/<alpha-value>)",
          300: "rgb(var(--color-error-300)/<alpha-value>)",
          400: "rgb(var(--color-error-400)/<alpha-value>)",
          500: "rgb(var(--color-error-500)/<alpha-value>)",
          600: "rgb(var(--color-error-600)/<alpha-value>)",
          700: "rgb(var(--color-error-700)/<alpha-value>)",
          800: "rgb(var(--color-error-800)/<alpha-value>)",
          900: "rgb(var(--color-error-900)/<alpha-value>)",
          950: "rgb(var(--color-error-950)/<alpha-value>)",
        },
        success: {
          0: "rgb(var(--color-success-0)/<alpha-value>)",
          50: "rgb(var(--color-success-50)/<alpha-value>)",
          100: "rgb(var(--color-success-100)/<alpha-value>)",
          200: "rgb(var(--color-success-200)/<alpha-value>)",
          300: "rgb(var(--color-success-300)/<alpha-value>)",
          400: "rgb(var(--color-success-400)/<alpha-value>)",
          500: "rgb(var(--color-success-500)/<alpha-value>)",
          600: "rgb(var(--color-success-600)/<alpha-value>)",
          700: "rgb(var(--color-success-700)/<alpha-value>)",
          800: "rgb(var(--color-success-800)/<alpha-value>)",
          900: "rgb(var(--color-success-900)/<alpha-value>)",
          950: "rgb(var(--color-success-950)/<alpha-value>)",
        },
        warning: {
          0: "rgb(var(--color-warning-0)/<alpha-value>)",
          50: "rgb(var(--color-warning-50)/<alpha-value>)",
          100: "rgb(var(--color-warning-100)/<alpha-value>)",
          200: "rgb(var(--color-warning-200)/<alpha-value>)",
          300: "rgb(var(--color-warning-300)/<alpha-value>)",
          400: "rgb(var(--color-warning-400)/<alpha-value>)",
          500: "rgb(var(--color-warning-500)/<alpha-value>)",
          600: "rgb(var(--color-warning-600)/<alpha-value>)",
          700: "rgb(var(--color-warning-700)/<alpha-value>)",
          800: "rgb(var(--color-warning-800)/<alpha-value>)",
          900: "rgb(var(--color-warning-900)/<alpha-value>)",
          950: "rgb(var(--color-warning-950)/<alpha-value>)",
        },
        info: {
          0: "rgb(var(--color-info-0)/<alpha-value>)",
          50: "rgb(var(--color-info-50)/<alpha-value>)",
          100: "rgb(var(--color-info-100)/<alpha-value>)",
          200: "rgb(var(--color-info-200)/<alpha-value>)",
          300: "rgb(var(--color-info-300)/<alpha-value>)",
          400: "rgb(var(--color-info-400)/<alpha-value>)",
          500: "rgb(var(--color-info-500)/<alpha-value>)",
          600: "rgb(var(--color-info-600)/<alpha-value>)",
          700: "rgb(var(--color-info-700)/<alpha-value>)",
          800: "rgb(var(--color-info-800)/<alpha-value>)",
          900: "rgb(var(--color-info-900)/<alpha-value>)",
          950: "rgb(var(--color-info-950)/<alpha-value>)",
        },
        typography: {
          0: "rgb(var(--color-typography-0)/<alpha-value>)",
          50: "rgb(var(--color-typography-50)/<alpha-value>)",
          100: "rgb(var(--color-typography-100)/<alpha-value>)",
          200: "rgb(var(--color-typography-200)/<alpha-value>)",
          300: "rgb(var(--color-typography-300)/<alpha-value>)",
          400: "rgb(var(--color-typography-400)/<alpha-value>)",
          500: "rgb(var(--color-typography-500)/<alpha-value>)",
          600: "rgb(var(--color-typography-600)/<alpha-value>)",
          700: "rgb(var(--color-typography-700)/<alpha-value>)",
          800: "rgb(var(--color-typography-800)/<alpha-value>)",
          900: "rgb(var(--color-typography-900)/<alpha-value>)",
          950: "rgb(var(--color-typography-950)/<alpha-value>)",
          white: "#FFFFFF",
          black: "#181718",
        },
        outline: {
          /* semantic border tier (design library) */
          subtle: "rgb(var(--color-outline-100)/<alpha-value>)",
          default: "rgb(var(--color-outline-200)/<alpha-value>)",
          strong: "rgb(var(--color-outline-300)/<alpha-value>)",
          emphasis: "rgb(var(--color-outline-700)/<alpha-value>)",
          /* SE-6622 Tier B graduation — accent-tinted CTA border ("+Create
           * new list"). Byte-identical alias of primary-300 (ex
           * needs-semantic-primary-300). */
          accent: "rgb(var(--color-primary-300)/<alpha-value>)",
          /* SE-5595: dedicated player-page card border — own isolated CSS
           * var, not a shared outline-* shade. */
          card: "rgb(var(--color-outline-card)/<alpha-value>)",
          0: "rgb(var(--color-outline-0)/<alpha-value>)",
          50: "rgb(var(--color-outline-50)/<alpha-value>)",
          100: "rgb(var(--color-outline-100)/<alpha-value>)",
          200: "rgb(var(--color-outline-200)/<alpha-value>)",
          300: "rgb(var(--color-outline-300)/<alpha-value>)",
          400: "rgb(var(--color-outline-400)/<alpha-value>)",
          500: "rgb(var(--color-outline-500)/<alpha-value>)",
          600: "rgb(var(--color-outline-600)/<alpha-value>)",
          700: "rgb(var(--color-outline-700)/<alpha-value>)",
          800: "rgb(var(--color-outline-800)/<alpha-value>)",
          900: "rgb(var(--color-outline-900)/<alpha-value>)",
          950: "rgb(var(--color-outline-950)/<alpha-value>)",
        },
        background: {
          0: "rgb(var(--color-background-0)/<alpha-value>)",
          50: "rgb(var(--color-background-50)/<alpha-value>)",
          100: "rgb(var(--color-background-100)/<alpha-value>)",
          200: "rgb(var(--color-background-200)/<alpha-value>)",
          300: "rgb(var(--color-background-300)/<alpha-value>)",
          400: "rgb(var(--color-background-400)/<alpha-value>)",
          500: "rgb(var(--color-background-500)/<alpha-value>)",
          600: "rgb(var(--color-background-600)/<alpha-value>)",
          700: "rgb(var(--color-background-700)/<alpha-value>)",
          800: "rgb(var(--color-background-800)/<alpha-value>)",
          900: "rgb(var(--color-background-900)/<alpha-value>)",
          950: "rgb(var(--color-background-950)/<alpha-value>)",
          error: "rgb(var(--color-background-error)/<alpha-value>)",
          warning: "rgb(var(--color-background-warning)/<alpha-value>)",
          muted: "rgb(var(--color-background-muted)/<alpha-value>)",
          success: "rgb(var(--color-background-success)/<alpha-value>)",
          info: "rgb(var(--color-background-info)/<alpha-value>)",
          light: "#FFFFFF",
          dark: "#181719",
          gray: "#1C1C1E",
        },
        indicator: {
          primary: "rgb(var(--color-indicator-primary)/<alpha-value>)",
          info: "rgb(var(--color-indicator-info)/<alpha-value>)",
          error: "rgb(var(--color-indicator-error)/<alpha-value>)",
        },
        prompt: {
          background: "#232323",
          buttonText: "#4693FF",
          border: "#626365",
        },
        chart: {
          player: "#0DA6F2",
          other: "#a855f7",
          horizontalLine: "#a2a3a3",
          // chart annex aliases (design library — docs/design-system/tokens.md)
          up: "#66B584",
          down: "#E77828",
          // GPR (Gemini Player Rating) data-viz pill: dark-teal fill + bright
          // blue readout. Static brand-viz colors, so they live in the closed
          // chart annex (not the semantic tier). Used by MobilePlayerCardView's
          // GPR pill. Keep ALLOWED_CHART_KEYS (eslint.config.local.ts) in sync.
          gpr: "#0d4d6b",
          gprStrong: "#5cb3e0",
        },
        /* Semantic tier (design library — docs/design-system/tokens.md).
         * App code may only use these names; the shade families above are
         * the raw palette tier that feeds them (atom-internal only). */
        content: {
          primary: "rgb(var(--color-typography-900)/<alpha-value>)",
          secondary: "rgb(var(--color-typography-700)/<alpha-value>)",
          "neutral-strong": "rgb(var(--color-typography-600)/<alpha-value>)",
          // SE-8550 / D4: own var — Light aliases typography-600, Dark typography-500.
          muted: "rgb(var(--color-content-muted)/<alpha-value>)",
          subtle: "rgb(var(--color-typography-400)/<alpha-value>)",
          disabled: "rgb(var(--color-typography-200)/<alpha-value>)",
          inverse: "rgb(var(--color-typography-0)/<alpha-value>)",
          "on-accent": "#FFFFFF",
        },
        surface: {
          base: "rgb(var(--color-background-0)/<alpha-value>)",
          raised: "rgb(var(--color-background-50)/<alpha-value>)",
          subtle: "rgb(var(--color-background-100)/<alpha-value>)",
          muted: "rgb(var(--color-background-200)/<alpha-value>)",
          strong: "rgb(var(--color-background-300)/<alpha-value>)",
          inverse: "rgb(var(--color-background-950)/<alpha-value>)",
          action: "rgb(var(--color-surface-action)/<alpha-value>)",
          /* SE-6622 Tier B graduations — ratified names for shades that were
           * parked under needs-semantic-*. Each aliases the EXACT var its
           * predecessor did, so the migration is byte-identical (no recolour).
           *   action-subtle  <- primary-100  (selected theme-option row bg)
           *   emphasis       <- background-800 (hover/selected dark fill)
           *   info-subtle    <- info-50      (GPR/watchlist highlight chip) */
          "action-subtle": "rgb(var(--color-primary-100)/<alpha-value>)",
          emphasis: "rgb(var(--color-background-800)/<alpha-value>)",
          "info-subtle": "rgb(var(--color-info-50)/<alpha-value>)",
          card: "rgb(var(--color-surface-card)/<alpha-value>)",
        },
        accent: {
          primary: "rgb(var(--color-accent-primary)/<alpha-value>)",
          /* SE-7550: the ground frame 29 binds as `accent/surface` — the
           * selected capture row and its inline editor. */
          surface: "rgb(var(--color-accent-surface)/<alpha-value>)",
          secondary: "rgb(var(--color-accent-secondary)/<alpha-value>)",
          selection: "rgb(var(--color-accent-selection)/<alpha-value>)",
          pressed: "rgb(var(--color-accent-pressed)/<alpha-value>)",
          hover: "rgb(var(--color-accent-hover)/<alpha-value>)",
        },
        /* BRAND ANNEX (SE-6800b) — brand identity, NOT interface semantics.
         *
         * Its own top-level group on purpose. The semantic-token budget
         * (eslint.config.local.ts) sums content + surface + accent + status +
         * the named outline keys and nothing else, so a brand colour parked
         * here does not consume a slot in a tier that is deliberately small and
         * already sitting at its ceiling. Same reasoning the RFC applies to the
         * `chart.*` annex: a data-viz or brand colour is not part of the
         * interface vocabulary app authors choose from, so it should not
         * compete for that vocabulary's budget.
         *
         * Keys here are NON-NUMERIC by rule. The raw-palette gate classifies a
         * group as raw palette when it holds numeric shade keys, so
         * `brand-wordmark` passes while a hypothetical `brand-500` would be
         * rejected exactly like `blue-500` — proven with an injection probe in
         * SE-6800b, not assumed.
         *
         * `wordmark` is the "Sports" half of the Gemini Sports sign-in lockup
         * ("Gemini" renders in content-primary). A FIXED literal, not a
         * theme-inverting var, because a brand lockup must read identically in
         * light and dark. The value is byte-identical to the `yellow-500` it
         * replaces (#EAB308) — this NAMES an existing colour rather than
         * choosing a new one, so rendering is provably unchanged.
         *
         * Minted rather than mapped because every ratified candidate was both
         * far and role-wrong: nearest was `chart-down` at Δ59 per channel, then
         * `status-warning` at Δ67 — and a brand mark is neither a downward data
         * series nor a warning state. The NAME needs design sign-off; the value
         * does not, because it did not change. */
        brand: {
          wordmark: "#EAB308",
        },
        status: {
          error: "rgb(var(--color-error-500)/<alpha-value>)",
          "error-surface": "rgb(var(--color-background-error)/<alpha-value>)",
          success: "rgb(var(--color-success-500)/<alpha-value>)",
          "success-surface":
            "rgb(var(--color-background-success)/<alpha-value>)",
          /* SE-6622 Tier B graduation — favourable-tone text/icon on a
           * success surface. Byte-identical alias of success-700 (ex
           * needs-semantic-success-700). */
          "success-strong": "rgb(var(--color-success-700)/<alpha-value>)",
          warning: "rgb(var(--color-warning-500)/<alpha-value>)",
          "warning-surface":
            "rgb(var(--color-background-warning)/<alpha-value>)",
          /* SE-6622 Tier B graduation — alert-tone icon/label (AlertTriangle,
           * conflict counts). Byte-identical alias of warning-600 (ex
           * needs-semantic-warning-600). */
          "warning-strong": "rgb(var(--color-warning-600)/<alpha-value>)",
          info: "rgb(var(--color-info-500)/<alpha-value>)",
          "info-surface": "rgb(var(--color-background-info)/<alpha-value>)",
          "info-strong": "rgb(var(--color-info-800)/<alpha-value>)",
          /* SE-7004 — the FINANCIAL objective tag (Club objectives modal, and
           * the Compliance Objectives column it drives). Sits in `status`
           * beside `info` because that family already holds semantic-meaning
           * colour that is not literally a state; `financial` is the label
           * colour and `financial-surface` its pale ground, the same
           * base/-surface shape as info. NOT a re-use of the info pair:
           * measured, `status-info-strong` is #075a83 and this is #1668dc —
           * a delta of (15,14,89), a different colour, not a rounding error. */
          financial: "rgb(var(--color-financial-500)/<alpha-value>)",
          "financial-surface":
            "rgb(var(--color-background-financial)/<alpha-value>)",
          "warning-icon-surface":
            "rgb(var(--color-status-warning-icon-surface)/<alpha-value>)",
        },
        /* RATING ANNEX (SE-6522) — score-band colours for the GPR / rating
         * badges, NOT general interface semantics.
         *
         * Its own top-level group for the same reasons as `brand.*` above: the
         * semantic-token budget (eslint.config.local.ts) sums content +
         * surface + accent + status + the named outline keys and nothing else,
         * and that tier is deliberately small and sitting at its ceiling. A
         * rating-band colour is data-viz vocabulary (which band a score falls
         * in), not the interface vocabulary app authors choose from, so it
         * does not compete for that budget — the RFC's chart/brand-annex
         * reasoning, applied to score bands.
         *
         * Keys here are NON-NUMERIC by rule: the raw-palette gate classifies a
         * group as raw palette when it directly holds numeric shade keys, so
         * `rating-positive-surface` passes while a hypothetical `rating-500`
         * would be rejected exactly like `blue-500`.
         *
         * Every value ALIASES the exact CSS var the rating badges already
         * rendered through (via needs-semantic-* aliases and the Badge atom's
         * raw-shade pairings), so this graduation NAMES existing colours
         * rather than choosing new ones — rendering is provably unchanged.
         * The underlying shades also remain as needs-semantic aliases for
         * their other consumers; this annex does not retire them.
         *
         * The annex is closed: ALLOWED_RATING_KEYS (eslint.config.local.ts)
         * polices the key set, exactly like ALLOWED_CHART_KEYS and
         * ALLOWED_BRAND_KEYS. Adding a key is a design decision. */
        rating: {
          "positive-surface": "rgb(var(--color-success-50)/<alpha-value>)",
          "positive-content": "rgb(var(--color-success-700)/<alpha-value>)",
          "positive-content-strong":
            "rgb(var(--color-success-950)/<alpha-value>)",
          "caution-surface": "rgb(var(--color-warning-50)/<alpha-value>)",
          "caution-content": "rgb(var(--color-warning-700)/<alpha-value>)",
          "negative-surface": "rgb(var(--color-error-50)/<alpha-value>)",
          "negative-content": "rgb(var(--color-error-700)/<alpha-value>)",
        },
        /* TRACKED SEMANTIC-TIER DEBT (SE-6622 Tier B).
         *
         * Every entry below is a semantic-tier ALIAS of a raw palette shade
         * that Figma has never given a semantic name. They exist so app code
         * can stop reaching into the raw palette tier directly without anyone
         * having to invent a colour value: each alias resolves to the SAME CSS
         * custom property as the shade it replaces, so the rename is provably
         * byte-identical and renders no differently.
         *
         * These are NOT ratified design tokens. The `needs-semantic-` prefix is
         * the burn-down marker: one prefix, always leading, so a single grep
         * returns an accurate count of the outstanding debt.
         *
         *   grep -rho "needs-semantic-[a-z]*-[0-9]*" src | sort | uniq -c
         *
         * When Figma defines a real semantic name for one of these shades,
         * delete the alias here and migrate its call sites to the ratified
         * token. The count only ever goes down.
         *
         * Rules for anyone editing this block:
         *   - An alias's value MUST stay `rgb(var(--color-<family>-<shade>)/
         *     <alpha-value>)` for the exact shade its NAME reproduces. Changing
         *     a value here is a design decision this block exists to avoid.
         *   - Never add an alias for a shade that already has a ratified
         *     equivalent in the semantic tier above (that is Tier A — rename
         *     the call site to the real token instead).
         *   - Never add an alias for a shade that lands within a rounding error
         *     of a ratified token in BOTH themes — that is drift, not debt.
         *     Nine such shades were collapsed onto their semantic neighbours
         *     (56 -> 47). The criterion, the measured deltas and the Δ=23 cliff
         *     that separates them from the surviving 47 are recorded in
         *     docs/design-system/tokens.md; collapsing shifts the rendered
         *     colour by at most 2 units per channel, which is a real change,
         *     not a no-op.
         *   - Adding entries needs a design decision; removing them does not.
         *
         * A lint rule banning raw palette classes must ANCHOR its match at the
         * class-token boundary, otherwise `text-needs-semantic-typography-800`
         * false-positives as `typography-600` (the SE-5527 substring trap).
         */
        "needs-semantic": {
          "background-600": "rgb(var(--color-background-600)/<alpha-value>)",

          "error-50": "rgb(var(--color-error-50)/<alpha-value>)",
          "error-100": "rgb(var(--color-error-100)/<alpha-value>)",
          "error-200": "rgb(var(--color-error-200)/<alpha-value>)",
          "error-300": "rgb(var(--color-error-300)/<alpha-value>)",
          "error-400": "rgb(var(--color-error-400)/<alpha-value>)",
          "error-600": "rgb(var(--color-error-600)/<alpha-value>)",
          "error-700": "rgb(var(--color-error-700)/<alpha-value>)",

          "info-100": "rgb(var(--color-info-100)/<alpha-value>)",
          "info-200": "rgb(var(--color-info-200)/<alpha-value>)",
          "info-300": "rgb(var(--color-info-300)/<alpha-value>)",
          "info-400": "rgb(var(--color-info-400)/<alpha-value>)",
          "info-600": "rgb(var(--color-info-600)/<alpha-value>)",
          "info-700": "rgb(var(--color-info-700)/<alpha-value>)",
          "info-950": "rgb(var(--color-info-950)/<alpha-value>)",

          "outline-600": "rgb(var(--color-outline-600)/<alpha-value>)",
          "outline-800": "rgb(var(--color-outline-800)/<alpha-value>)",

          "primary-50": "rgb(var(--color-primary-50)/<alpha-value>)",
          "primary-400": "rgb(var(--color-primary-400)/<alpha-value>)",
          "primary-500": "rgb(var(--color-primary-500)/<alpha-value>)",
          "primary-600": "rgb(var(--color-primary-600)/<alpha-value>)",
          "primary-900": "rgb(var(--color-primary-900)/<alpha-value>)",

          "secondary-100": "rgb(var(--color-secondary-100)/<alpha-value>)",
          "secondary-300": "rgb(var(--color-secondary-300)/<alpha-value>)",
          "secondary-700": "rgb(var(--color-secondary-700)/<alpha-value>)",

          "success-50": "rgb(var(--color-success-50)/<alpha-value>)",
          "success-100": "rgb(var(--color-success-100)/<alpha-value>)",
          "success-200": "rgb(var(--color-success-200)/<alpha-value>)",
          "success-300": "rgb(var(--color-success-300)/<alpha-value>)",
          "success-400": "rgb(var(--color-success-400)/<alpha-value>)",
          "success-600": "rgb(var(--color-success-600)/<alpha-value>)",
          "success-800": "rgb(var(--color-success-800)/<alpha-value>)",

          "typography-800": "rgb(var(--color-typography-800)/<alpha-value>)",

          "warning-50": "rgb(var(--color-warning-50)/<alpha-value>)",
          "warning-100": "rgb(var(--color-warning-100)/<alpha-value>)",
          "warning-200": "rgb(var(--color-warning-200)/<alpha-value>)",
          "warning-300": "rgb(var(--color-warning-300)/<alpha-value>)",
          "warning-700": "rgb(var(--color-warning-700)/<alpha-value>)",
          "warning-900": "rgb(var(--color-warning-900)/<alpha-value>)",
        },
        ...customColors,
      },
      fontFamily: {
        heading: ["SatoshiBold", "sans-serif"],
        body: ["Satoshi", "sans-serif"],
        medium: ["SatoshiMedium", "sans-serif"],
        mono: undefined,
        bebas: ["BebasNeue", "sans-serif"],
      },
      fontWeight: {
        extrablack: "950",
      },
      fontSize: {
        "2xs": "10px",
        "10xl": "10rem",
        /* Ratified text styles (design library — docs/design-system/tokens.md).
         * Values alias today's Tailwind sizes exactly; the Text atom bundles
         * family/weight on top. */
        /* `micro` was the ONLY rung on this ladder without a line-height, which
         * made it the only one that inherits. On web that is a real defect, not
         * a nicety: CSS line-height inherits, so a `text-micro` chip inside a
         * container that sets its own leading takes the CONTAINER's line box —
         * the pipeline table's cells carry `leading-[22px]`, which wrapped a
         * 22px line box around a 10px glyph and made every micro chip's fill
         * read three times taller than its text (SE-6554, 2026-08-07). 14px
         * keeps the ladder's own ~1.33 ratio (12/16, 14/20, 16/24, 18/28). */
        micro: ["10px", { lineHeight: "14px" }],
        caption: ["12px", { lineHeight: "16px" }],
        body: ["14px", { lineHeight: "20px" }],
        "title-sm": ["16px", { lineHeight: "24px" }],
        title: ["18px", { lineHeight: "28px" }],
        "title-lg": ["20px", { lineHeight: "28px" }],
        "display-sm": ["24px", { lineHeight: "32px" }],
        "display-md": ["30px", { lineHeight: "36px" }],
        "display-lg": ["36px", { lineHeight: "40px" }],
        "display-xl": ["48px", { lineHeight: "48px" }],
        "display-2xl": ["60px", { lineHeight: "60px" }],
      },
      spacing: {
        /* named 2px step (ratified decision #3) — a name, not a fraction */
        micro: "2px",
        /* TRACKED SPACING/SIZING DEBT (SE-6782).
         *
         * The same holding position the colour tier took in SE-6622 Tier B,
         * applied to the spacing/sizing axes: each entry below carries the
         * EXACT value a call site asks for and the closed ladder has no step
         * for. They exist so those call sites can name a token instead of
         * forcing the value through a style escape hatch — without anyone
         * having to approximate. Snapping to the nearest rung was considered
         * and rejected: 240px has no neighbour (the ladder tops out at 64px)
         * and 10px sits between 8px and 12px, so either snap would have been
         * an unreviewed visual change.
         *
         * These are NOT ratified design tokens. The `needs-semantic-` prefix is
         * the SAME burn-down marker the colour aliases use — one prefix,
         * always leading, so a single grep counts colour and spacing debt
         * together:
         *
         *   grep -rhoE --exclude-dir=__tests__ \
         *     "needs-semantic-[a-z0-9-]+" src | sort | uniq -c
         *
         * Both qualifiers matter. `+` (not `*`) because an empty suffix lets
         * the bare marker match itself, scoring every mention of the prefix —
         * the MARKER constant and the invariant tests' regex literals included
         * — as debt. `--exclude-dir=__tests__` because a class named in an
         * assertion is not a rendered call site; the invariant tests hardcode
         * both class names on purpose so a rename fails loudly. The
         * offScaleUtilities gate skips __tests__ for the same reason.
         *
         * A prefix counter reads raw text, so keep the marker out of prose and
         * let it appear only in executable code.
         *
         * Unlike the colour block — which nests under a `needs-semantic` GROUP
         * key, so `bg-needs-semantic-primary-500` is composed by Tailwind — the
         * spacing scale is flat, so the prefix has to live in the key itself.
         * The grep above is what makes the two shapes count as one debt.
         *
         * Rules for anyone editing this block:
         *   - A token's value MUST stay the exact px its NAME reproduces.
         *     Changing a value here is the visual decision this block exists
         *     to avoid.
         *   - Never add a token for a value that already has a rung on the
         *     closed scale (snap the call site to the real step instead).
         *   - Adding entries needs a design decision; removing them does not.
         *
         * When the scale properly covers one of these, delete the token here
         * and migrate its call sites to the ratified step. The count only
         * ever goes down.
         */
        "needs-semantic-10px": "10px",
        "needs-semantic-240px": "240px",
      },
      /* SE-8486 retired the SE-8291 card tokens read from node 97:1433
       * (`gap-pitch-player-list` 3, `py-pitch-node-y` 6,
       * `rounded-pitch-position-node` 10): the approved card 1010:20480 sits
       * on the closed ladder (gap-1, p-2, rounded-md). */
      padding: {
        /* SE-8291 — exact vertical inset supplied for the Pitch panels. */
        "pitch-panel-section-y": "18px",
        /* SE-8486 — the position pop-out's top anchor (Figma 947:16436 /
         * 383:5278, rows W70/S32): the box sits 120 px from the top of the
         * viewport, never vertically centred. The spacing scale stops at
         * 64; axis-specific and component-named so it cannot become a
         * generic rung. */
        "pitch-popout-top": "120px",
      },
      aspectRatio: {
        /* SE-8486 — the pitch surface's proportion (Figma 281:2911, row W42:
         * 570×694). Component-named: the surface keeps this ratio and
         * centres in whatever column width it gets. */
        "pitch-surface": "570 / 694",
      },
      letterSpacing: {
        "formation-label": "0.6px",
      },
      boxShadow: {
        "hard-1": "-2px 2px 8px 0px rgba(38, 38, 38, 0.20)",
        "hard-2": "0px 3px 10px 0px rgba(38, 38, 38, 0.20)",
        "hard-3": "2px 2px 8px 0px rgba(38, 38, 38, 0.20)",
        "hard-4": "0px -3px 10px 0px rgba(38, 38, 38, 0.20)",
        "hard-5": "0px 2px 10px 0px rgba(38, 38, 38, 0.10)",
        "soft-1": "0px 0px 10px rgba(38, 38, 38, 0.1)",
        "soft-2": "0px 0px 20px rgba(38, 38, 38, 0.2)",
        "soft-3": "0px 0px 30px rgba(38, 38, 38, 0.1)",
        "soft-4": "0px 0px 40px rgba(38, 38, 38, 0.1)",
      },
    },
  },
  plugins: [],
};
